import { RefreshCcw } from "lucide-react";
import { useCallback, useState } from "react";
import { Navigate, useSearchParams } from "react-router-dom";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { ColocaSkeleton } from "@/shared/components/feedback/ColocaSkeleton";
import { ColocaPageHeader } from "@/shared/components/layout/ColocaPageHeader";
import useTranslations from "@/shared/lang/useTranslations";

import { OnboardingVerificationFlow } from "../components/OnboardingVerificationFlow";
import { VerificationLayout } from "../components/VerificationLayout";
import { VerificationTypeChoice } from "../components/VerificationTypeChoice";
import { useOrganizationVerificationStatus } from "../hooks/useOrganizationVerificationStatus";
import type { VerificationPurpose } from "../types/verification-api.types";

/** `?replace=1` — reached from Settings: verify the account with ANOTHER identity (person or company). */
const REPLACE_PARAM = "replace";

/**
 * Marco A — authenticated onboarding. Renders inside the dashboard shell (DashboardLayout provides the
 * `ColocaAppShell`). Bootstraps from `GET /internal/account/verification`: an existing corporate identity
 * resumes `OnboardingVerificationFlow` with ITS purpose (KYC or KYB); without one — or when Settings asked to
 * switch identity (`?replace=1`) — the organization first chooses how it verifies itself and the flow mounts
 * with that purpose. Creating the new corporate identity re-points the organization to it, so the previous
 * verification stops applying and payouts stay blocked until the new one is approved. Default export
 * (routed).
 */
export default function OnboardingVerificationPage() {
  const { t } = useTranslations("verification");
  const [searchParams, setSearchParams] = useSearchParams();
  const replacing = searchParams.get(REPLACE_PARAM) === "1";
  const { isOrgMember, isLoading, data, hasCorporateIdentity, purpose, identityId, kycStatus, refresh } =
    useOrganizationVerificationStatus();
  const [chosen, setChosen] = useState<VerificationPurpose | null>(null);

  const handleChoose = useCallback(
    (next: VerificationPurpose) => {
      setChosen(next);
      // Drop `?replace=1` once the new flow starts: a refresh after the identity is created must resume THAT
      // identity, not offer the choice again.
      if (replacing) {
        const params = new URLSearchParams(searchParams);
        params.delete(REPLACE_PARAM);
        setSearchParams(params, { replace: true });
      }
    },
    [replacing, searchParams, setSearchParams],
  );

  // Internal staff have no organization to verify; the route is only reached from member surfaces.
  if (!isOrgMember) return <Navigate to="/app/wallets" replace />;

  const header = <ColocaPageHeader title={t("onboarding_title")} description={t("onboarding_description")} />;

  if (isLoading && data === null) {
    return (
      <div className="space-y-6">
        {header}
        <ColocaSkeleton className="mx-auto h-72 w-full max-w-[640px]" />
      </div>
    );
  }

  const retryAlert = (
    <div className="space-y-6">
      {header}
      <ColocaAlert
        variant="destructive"
        text={t("error_generic_description")}
        actions={
          <ColocaButton size="sm" variant="default" onClick={refresh}>
            {t("button_retry")}
          </ColocaButton>
        }
      />
    </div>
  );

  // Never offer the KYC/KYB choice on a failed read: a second `is_organization_identity` identity re-points
  // the organization, so it must be a deliberate act (first time, or an explicit switch from Settings).
  if (data === null) return retryAlert;

  if (chosen) {
    return <OnboardingVerificationFlow key={chosen} purpose={chosen} />;
  }

  if (hasCorporateIdentity && !replacing) {
    if (!purpose) return retryAlert;
    return (
      <OnboardingVerificationFlow
        key={identityId ?? purpose}
        purpose={purpose}
        identityId={identityId}
        initialStatus={kycStatus}
      />
    );
  }

  return (
    <div className="space-y-6">
      {header}
      {replacing && hasCorporateIdentity && (
        <ColocaAlert
          variant="warning"
          icon={RefreshCcw}
          heading={t("replace_warning_title")}
          text={t("replace_warning_description")}
        />
      )}
      <VerificationLayout size="business">
        <VerificationTypeChoice onChoose={handleChoose} />
      </VerificationLayout>
    </div>
  );
}
